import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import logoBlack from '../../../assets/images/this+that_logo_black.svg';
import logoWhite from '../../../assets/images/this+that_logo_white.svg';
import { Theme, useTheme } from '../../../context/ThemeContext';
import { AuthThemeToggle } from '../../AuthThemeToggle';
import { AuthBrandPanel } from '../AuthBrandPanel';

interface AuthLayoutProps {
  brandPanel?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  hideMobileLogo?: boolean;
}

const stylesheet = {
  root: 'h-screen w-full overflow-hidden grid grid-cols-1 min-[880px]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:grid-cols-[minmax(0,65fr)_minmax(0,35fr)]',
  leftPanel: 'hidden min-[880px]:block',
  rightPanel:
    'relative flex flex-col overflow-y-auto bg-semantic-bg-page px-6 py-[34px] min-[880px]:p-12',
  themeToggle: 'absolute top-6 right-7',
  contentWrapper:
    'flex flex-col justify-center w-full min-[880px]:max-w-[400px] mx-auto py-10 min-[880px]:flex-1',
  footerWrapper: 'w-full min-[880px]:max-w-[400px] mx-auto shrink-0 pt-6',
  mobileLogo:
    'min-[880px]:hidden flex-1 flex items-center justify-center transition-opacity duration-300 ease',
  mobileLogoHidden: 'opacity-0 pointer-events-none',
  mobileLogoVisible: 'opacity-100',
  mobileLogoImg: 'h-[26px] w-auto',
  mobileLogoSpacer: 'min-[880px]:hidden flex-1',
};

export function AuthLayout({
  brandPanel = <AuthBrandPanel />,
  children,
  footer,
  hideMobileLogo = false,
}: Readonly<AuthLayoutProps>): ReactNode {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const logoSrc = theme === Theme.DARK ? logoWhite : logoBlack;

  return (
    <div className={stylesheet.root}>
      <div className={stylesheet.leftPanel}>{brandPanel}</div>
      <div className={stylesheet.rightPanel}>
        <div className={stylesheet.themeToggle}>
          <AuthThemeToggle />
        </div>
        <div
          className={`${stylesheet.mobileLogo} ${hideMobileLogo ? stylesheet.mobileLogoHidden : stylesheet.mobileLogoVisible}`}
          aria-hidden={hideMobileLogo}
        >
          <img
            src={logoSrc}
            alt={t('auth.logoAlt')}
            className={stylesheet.mobileLogoImg}
          />
        </div>
        <div className={stylesheet.contentWrapper}>{children}</div>
        <div className={stylesheet.mobileLogoSpacer} aria-hidden="true" />
        {footer && <div className={stylesheet.footerWrapper}>{footer}</div>}
      </div>
    </div>
  );
}
